import { Box, Button, Table as ChakraTable, Text } from "@chakra-ui/react";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import type { TableProps } from "@/ui/tables/Table";
import { useState, useCallback } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { LuPlus } from "react-icons/lu";
import CreateModalProductKit from "./CreateModalProductKit";
import EditModalProductKit from "./EditModalProductKit";
import { useUpdateKit } from "@/core/kits/useCases/useUpdateKit";
import { useGetKits } from "@/shared/core-product-kits/useCases/useGetKits";
import { Kit } from "@/shared/core-product-kits/types/Kit";
import { Pagination } from "@/shared/core-configs";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useAuth } from "@/shared/session";
import { useGetStorage } from "@/core/storages/useCases/useGetStorage";
import { PageContainer } from "@/ui/containers/PageContainer";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
} as const;

const getColumns = ({
  handleOpenViewProductsModal,
  handleToggleEnabled,
}: {
  handleOpenViewProductsModal: (kit: Kit) => void;
  handleToggleEnabled: (kit: Kit) => void;
  handleOpenEditModal: (kit: Kit) => void;
}): TableProps<Kit>["headers"] => [
  {
    label: "Nombre del kit",
    itemLabel: (item) => <Text fontWeight="semibold">{item.name}</Text>,
  },
  {
    label: "Bodega",
    itemLabel: (item) => item.storage?.name || "-",
  },
  {
    label: "Productos",
    itemLabel: (item) => (
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleOpenViewProductsModal(item)}
      >
        Ver productos ({item.products.length})
      </Button>
    ),
  },
  {
    label: "Estado",
    itemLabel: (item) => (
      <Button
        variant="ghost"
        size="sm"
        colorPalette={item.enabled ? "gray" : "green"}
        color={item.enabled ? "fg.muted" : undefined}
        onClick={() => handleToggleEnabled(item)}
      >
        {item.enabled ? "Deshabilitar" : "Habilitar"}
      </Button>
    ),
  },
];

export const TableProductKit = () => {
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isViewProductsModalOpen, setViewProductsModalOpen] = useState(false);
  const [selectedProductKit, setSelectedProductKit] = useState<Kit | null>(
    null,
  );
  const [showDisabled, setShowDisabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { session } = useAuth();

  const { data: storages } = useGetStorage(session?.zone ?? "");

  const {
    data: productKits,
    isPending,
    pagination: metaProductKits,
  } = useGetKits({
    showDisabled,
    page: pagination.page,
    pageSize: pagination.pageSize,
    searchTerm: debouncedSearchTerm,
    storageId: storages?.map((storage) => storage.id) ?? [],
  });
  const { mutate: updateKit } = useUpdateKit();

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = async (kit: Kit) => {
    const ok = await confirmToggle(kit.enabled, `el kit "${kit.name}"`);
    if (!ok) return;
    updateKit(
      {
        id: kit.id,
        data: {
          enabled: !kit.enabled,
        },
      },
      {
        onSuccess: () =>
          notify.success(kit.enabled ? "Kit deshabilitado" : "Kit habilitado"),
        onError: (error) => notify.error("No se pudo actualizar el kit", error),
      },
    );
  };

  const handleOpenCreateModal = useCallback(() => setCreateModalOpen(true), []);
  const handleCloseCreateModal = useCallback(
    () => setCreateModalOpen(false),
    [],
  );

  const handleOpenEditModal = useCallback((kit: Kit) => {
    setSelectedProductKit(kit);
    setEditModalOpen(true);
  }, []);
  const handleCloseEditModal = useCallback(() => {
    setSelectedProductKit(null);
    setEditModalOpen(false);
  }, []);

  const handleOpenViewProductsModal = useCallback((kit: Kit) => {
    setSelectedProductKit(kit);
    setViewProductsModalOpen(true);
  }, []);
  const handleCloseViewProductsModal = useCallback(() => {
    setSelectedProductKit(null);
    setViewProductsModalOpen(false);
  }, []);

  const columns = getColumns({
    handleOpenViewProductsModal,
    handleToggleEnabled,
    handleOpenEditModal,
  });

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Kits de productos"
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear kit de Producto
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por nombre del kit…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        emptyDescription="Un kit agrupa varios productos para agregarlos a un vale de una sola vez."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear kit de Producto
          </Button>
        }
        headers={columns}
        data={productKits}
        extractItemKey={(item) => item.id}
        pages={metaProductKits?.totalPages}
        totalItems={metaProductKits?.count}
        itemsLabel="kits"
        showPagination
        showSearch
        showDisabledCheck
        onShowDisabledChange={setShowDisabled}
        onSearch={(search) => setSearchTerm(search)}
        onEdit={(item) => handleOpenEditModal(item)}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="kitProducts"
      />

      <CreateModalProductKit
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />

      {selectedProductKit && (
        <EditModalProductKit
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          selectedKit={selectedProductKit}
        />
      )}

      <DialogRoot
        isOpen={isViewProductsModalOpen}
        onClose={handleCloseViewProductsModal}
        size="xl"
        isCentered
      >
        <DialogContent maxW="5xl">
          <DialogHeader borderBottomWidth="1px" borderColor="border">
            <DialogTitle>
              Productos del kit: {selectedProductKit?.name}
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody pt={4}>
            <Box borderWidth="1px" borderColor="border" rounded="lg">
              <ChakraTable.ScrollArea>
                <ChakraTable.Root size="sm" variant="line">
                  <ChakraTable.Header bg="bg.subtle">
                    <ChakraTable.Row>
                      <ChakraTable.ColumnHeader {...headerProps}>
                        ID Producto
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader {...headerProps}>
                        Código
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader {...headerProps}>
                        Almacén
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader
                        {...headerProps}
                        textAlign="end"
                      >
                        Cantidad
                      </ChakraTable.ColumnHeader>
                    </ChakraTable.Row>
                  </ChakraTable.Header>
                  <ChakraTable.Body>
                    {selectedProductKit?.products?.map((product) => (
                      <ChakraTable.Row key={product.id}>
                        <ChakraTable.Cell fontFamily="mono" fontSize="xs">
                          {product.id}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell fontWeight="semibold">
                          {product.externalProduct?.code ??
                            product.erpProduct?.erpCode}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell>
                          {product.storage?.name}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell textAlign="end">
                          {product.kitProduct.quantity}
                        </ChakraTable.Cell>
                      </ChakraTable.Row>
                    ))}
                    {selectedProductKit?.products.length === 0 && (
                      <ChakraTable.Row>
                        <ChakraTable.Cell
                          colSpan={4}
                          textAlign="center"
                          py={8}
                          color="fg.muted"
                        >
                          No hay productos asignados a este kit.
                        </ChakraTable.Cell>
                      </ChakraTable.Row>
                    )}
                  </ChakraTable.Body>
                </ChakraTable.Root>
              </ChakraTable.ScrollArea>
            </Box>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </PageContainer>
  );
};
